import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import AboutProcess from './components/AboutProcess';
import ServiceArea from '../home/components/ServiceArea';
import CTASection from '../home/components/CTASection';
import Gallery from '../home/components/Gallery';
import FAQ from '../home/components/FAQ';
import ConsultationForm from '../home/components/ConsultationForm';

const values = [
  {
    icon: 'ri-hand-heart-line',
    title: 'Craftsmanship First',
    description: 'Every stitch is intentional. Our tailors follow time-honored British techniques honed over generations — applied to every garment, every time.',
    image: 'https://public.readdy.ai/ai/img_res/b5206e737fdaa0e769733c34ddbb2b67.jpg',
  },
  {
    icon: 'ri-vip-diamond-line',
    title: 'Exceptional Materials',
    description: 'We partner exclusively with the world\'s finest mills — Loro Piana, Scabal, Holland & Sherry, and Dormeuil — to ensure every fabric we use meets our uncompromising standards.',
    image: 'https://readdy.ai/api/search-image?query=collection%20of%20fine%20premium%20suit%20fabrics%20swatches%20in%20deep%20navy%20charcoal%20and%20warm%20grey%20tones%20displayed%20elegantly%20on%20a%20polished%20wooden%20surface%2C%20luxury%20textile%20detail%2C%20soft%20diffused%20lighting%2C%20refined%20tailoring%20showroom%20atmosphere&width=500&height=400&seq=abt2-fab-02&orientation=landscape',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Client-Centered Always',
    description: 'Your vision shapes every decision we make. We listen deeply, advise honestly, and execute with precision — ensuring the end result is a garment that exceeds every expectation.',
    image: 'https://readdy.ai/api/search-image?query=tailor%20and%20client%20engaged%20in%20a%20warm%20professional%20consultation%20in%20an%20elegant%20showroom%2C%20fabric%20swatches%20on%20the%20table%2C%20natural%20light%2C%20genuine%20connection%20and%20attentiveness%2C%20refined%20luxury%20interior%20with%20warm%20tones&width=500&height=400&seq=abt2-client-03&orientation=landscape',
  },
];

const differentiators = [
  {
    icon: 'ri-history-line',
    title: '55+ Years of Mastery',
    description: 'Founded in 1969, we bring over five decades of institutional knowledge to every garment — experience that simply cannot be replicated.',
  },
  {
    icon: 'ri-building-line',
    title: 'Savile Row Heritage',
    description: 'Our craft is rooted in the traditions of London\'s Savile Row, the birthplace of modern bespoke tailoring — now available to clients across America.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Truly Nationwide',
    description: 'We are the only premium custom tailor in the country with an established house call network serving all 50 states — no competitor matches this reach.',
  },
  {
    icon: 'ri-group-line',
    title: 'Family Legacy',
    description: 'Two generations of the Narwani family have built this business on personal relationships, not transactions — every client is treated as family.',
  },
  {
    icon: 'ri-scissors-2-line',
    title: 'No Shortcuts, Ever',
    description: 'We do not offer off-the-shelf or made-to-measure from templates. Every garment is fully bespoke, built from scratch using your unique measurements.',
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'Satisfaction Guaranteed',
    description: 'If your garment does not meet your expectations at any stage, we refine it until it does. Your satisfaction is non-negotiable.',
  },
];

const stats = [
  { value: '1969', label: 'Year Founded' },
  { value: '55+', label: 'Years in Business' },
  { value: '5,000+', label: 'Garments Crafted' },
  { value: '500+', label: 'Fabrics Available' },
];

const milestones = [
  {
    year: '1969',
    title: 'The Beginning',
    desc: 'Founded in Boston by master tailor Harbhajan Narwani, trained in the finest British tailoring traditions. His mission: bring Savile Row craftsmanship to discerning American clients.',
  },
  {
    year: '1985',
    title: 'Going Nationwide',
    desc: 'Pioneered our house call and office call service, making bespoke tailoring accessible to clients across the entire country — a first in the industry.',
  },
  {
    year: '1998',
    title: 'Flagship Studio Opens',
    desc: 'Opened our Wellesley Hills, Massachusetts studio — a dedicated showroom that became the region\'s premier destination for bespoke garments.',
  },
  {
    year: '2010',
    title: 'Next Generation',
    desc: 'The second generation of the Narwani family joined the business, blending the company\'s deep heritage with contemporary style and modern client needs.',
  },
  {
    year: '2025',
    title: 'Still Going Strong',
    desc: 'Over 5,000 satisfied clients, 500+ fabrics, and a growing nationwide network of consultants — still committed to the same perfect fit we have promised since day one.',
  },
];

export default function AboutPage() {
  const scrolled = true;

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* ── 1. HERO ── */}
      <section className="relative min-h-[580px] pt-32 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=timeless%20British%20tailoring%20atelier%20interior%20with%20vintage%20wooden%20cabinets%20full%20of%20rich%20fabric%20bolts%2C%20antique%20measuring%20tape%20and%20shears%20on%20display%2C%20warm%20amber%20candescent%20lighting%2C%20decades%20of%20heritage%20visible%20in%20every%20detail%2C%20distinguished%20and%20prestigious%20craftsmanship%20atmosphere&width=1440&height=580&seq=abt2-hero-v2&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/55 to-black/65" />
        <div className="relative z-10 text-center w-full px-4 py-16">
          <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-5">Est. 1969</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Crafting Excellence <br /><span className="text-[#C9A961]">For Over 55 Years</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            The story of a family, a craft, and an unwavering commitment to the perfect fit — built one garment at a time since 1969
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-[#C9A961] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Book a Consultation
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center border border-white/40 text-white px-10 py-4 rounded-full text-base font-semibold hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Services
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 2. TRUST BAR ── */}
      <section className="py-10 bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-[#C9A961] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{stat.value}</div>
                <div className="text-xs text-gray-400 font-medium tracking-wide uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. SERVICES — Three Pillars of Our Craft ── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">What Drives Us</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Three Pillars of Our <span className="text-[#C9A961]">Craft</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {values.map((val, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 group hover:border-[#C9A961]/30 transition-all duration-300">
                <div className="h-52 overflow-hidden">
                  <img
                    src={val.image}
                    alt={val.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#C9A961] to-[#B89851] rounded-xl mb-5">
                    <i className={`${val.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {val.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{val.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. WHY CHOOSE US — What Makes Us Different ── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">Our Difference</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Clients Choose <span className="text-[#C9A961]">British Custom Tailors</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              There are many tailors. There is only one British Custom Tailors.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-[#C9A961]/40 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/20 rounded-xl mb-5">
                  <i className={`${item.icon} text-2xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. PROCESS ── */}
      <AboutProcess />

      {/* ── 6. ABOUT — Brand Story & Timeline ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-4">Our Story</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                A Legacy Built on the <span className="text-[#C9A961]">Perfect Fit</span>
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed">
                <p>
                  British Custom Tailors was founded in 1969 by <strong className="text-gray-800">Harbhajan Narwani</strong>, a master tailor who trained in the finest traditions of British tailoring. His vision was bold but clear: bring the uncompromising craftsmanship of Savile Row to the American client — and make it personal.
                </p>
                <p>
                  He built the business not on volume, but on relationships. Every client was treated as an individual — measured, listened to, and clothed with extraordinary care. Word spread, and with it, a reputation for garments that fit not just the body, but the life of the person wearing them.
                </p>
                <p>
                  Over half a century later, the second generation of the Narwani family continues that legacy — blending deep heritage with a modern sensibility. The tools have evolved, the fabric library has grown, but the commitment to the perfect fit remains unchanged.
                </p>
                <p>
                  Today, British Custom Tailors serves clients from Boston to Los Angeles through our flagship Wellesley Hills studio and our pioneering <strong className="text-gray-800">nationwide house call service</strong> — the only one of its kind in the premium tailoring space.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/services"
                  className="inline-flex items-center bg-[#C9A961] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Discover Our Services
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden h-[580px] w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=experienced%20British%20master%20tailor%20standing%20confidently%20in%20a%20distinguished%20tailoring%20workshop%2C%20warm%20amber%20studio%20lighting%2C%20bolts%20of%20fine%20fabric%20on%20shelves%20behind%2C%20decades%20of%20expertise%20conveyed%20through%20composed%20demeanor%2C%20artisan%20heritage%20and%20family%20legacy%20atmosphere&width=700&height=580&seq=abt2-story-main&orientation=portrait"
                  alt="British Custom Tailors — 55 years of heritage and craftsmanship"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 border border-gray-100 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#C9A961] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-fill text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Est. 1969</div>
                    <div className="text-sm text-gray-500">Family-owned & operated</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">Our Journey</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Five Decades of <span className="text-[#C9A961]">Milestones</span>
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-[#C9A961]/20"></div>
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-8 items-start group">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-full bg-[#C9A961] text-white font-bold text-sm z-10 group-hover:scale-110 transition-transform" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {m.year.slice(2)}
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 flex-1 group-hover:border-[#C9A961]/30 transition-all">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#C9A961] font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{m.year}</span>
                      <span className="text-gray-900 font-semibold">{m.title}</span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. TESTIMONIALS ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#C9A961] rounded-full mx-auto mb-8">
            <i className="ri-double-quotes-l text-3xl text-white"></i>
          </div>
          <blockquote className="text-2xl sm:text-3xl font-light text-gray-800 leading-relaxed mb-8 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
            "I have worn suits from the finest tailors in London and New York. British Custom Tailors is in a different league — the fit, the fabric, the service. I have been a client for over 20 years and have never been disappointed."
          </blockquote>
          <div className="text-[#C9A961] font-semibold">— James R., Senior Partner, Boston</div>
        </div>
      </section>

      {/* ── 8. SERVICE AREA ── */}
      <ServiceArea />

      {/* ── 9. CTA SECTION ── */}
      <CTASection />

      {/* ── 10. GALLERY ── */}
      <Gallery />

      {/* ── 11. SPECIAL OFFER / PAGE CTA ── */}
      <section className="py-24 bg-[#C9A961]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Experience the Difference
          </h2>
          <p className="text-xl text-white/85 mb-10">
            Join thousands of clients who have trusted us with their most important garments — for over 55 years.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-white text-[#C9A961] px-10 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 866-857-8487
            </a>
            <Link
              to="/locations"
              className="inline-flex items-center justify-center border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Find a Location Near You
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 12. FAQ ── */}
      <FAQ />

      {/* ── 13. CONSULTATION FORM ── */}
      <ConsultationForm />

      <Footer />
    </div>
  );
}
